import React, { useEffect, useState } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Flag,
  Building,
  Link2,
  Layers,
  FileText,
  BriefcaseBusiness,
  Share2,
  Clock,
  MessageCircle,
  ShieldCheck,
  CreditCard,
  Eye
} from "lucide-react";
import api from "../../../api/axiosInstance";
import { useSelector } from "react-redux";


const imgUrl = (path) => {
  if (!path) return null;
  const base = (process.env.REACT_APP_IMAGE_BASE_URL || '').replace(/\/+$/, '');
  const clean = path.replace(/^\/+/, '');
  return `${base}/${clean}`;
};

const MerchantProfile = () => {
  const accessToken = useSelector((s) => s.merchant.accessToken);

  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);

  // --------------------------
  // Fetch Merchant Inquiry Profile
  // --------------------------
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("api/merchant/merchant-inquiry");
        setMerchant(res.data.data);
      } catch (err) {
        console.error("Profile fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) fetchProfile();
  }, [accessToken]);

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '400px',
      fontSize: '16px',
      color: 'var(--color-text-light)'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid var(--color-secondary)',
          borderTop: '4px solid var(--color-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <span>Loading profile...</span>
      </div>
    </div>
  );

  if (!merchant)
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        padding: '20px'
      }}>
        <div style={{
          padding: '24px 32px',
          backgroundColor: 'var(--color-danger-light)',
          color: 'var(--color-danger)',
          borderRadius: '12px',
          border: '1px solid var(--color-danger)',
          fontSize: '16px',
          fontWeight: '500'
        }}>
          Failed to load your profile.
        </div>
      </div>
    );

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .stunning-profile-container {
          background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 50%, #f5f3ff 100%);
          min-height: 100vh;
          padding: 32px 24px;
        }

        .stunning-profile-wrapper {
          max-width: 1400px;
          margin: 0 auto;
        }

        .stunning-hero-section {
          display: grid;
          grid-template-columns: 380px 1fr;
          gap: 32px;
          margin-bottom: 32px;
          animation: fadeInUp 0.6s ease-out;
        }

        .stunning-profile-card {
          background: white;
          border-radius: 24px;
          padding: 40px 32px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(96, 82, 165, 0.12);
          border: 1px solid rgba(96, 82, 165, 0.1);
          position: relative;
          overflow: hidden;
          animation: slideInLeft 0.7s ease-out;
        }

        .stunning-profile-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 140px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
          border-radius: 24px 24px 0 0;
        }

        .stunning-profile-image {
          position: relative;
          z-index: 2;
          width: 140px;
          height: 140px;
          margin: 0 auto 24px;
        }

        .stunning-photo-wrapper {
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15);
          border: 5px solid white;
          overflow: hidden;
          animation: scaleIn 0.8s ease-out;
        }

        .stunning-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .stunning-photo-icon {
          color: var(--color-primary);
        }

        .stunning-profile-name {
          font-size: 28px;
          font-weight: 700;
          color: var(--color-text-dark);
          margin-bottom: 20px;
          position: relative;
          z-index: 2;
        }

        .stunning-contact-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 20px;
          background: #cfc9ff3b;
          border-radius: 12px;
          margin-bottom: 12px;
          transition: all 0.3s ease;
          position: relative;
          z-index: 2;
        }

        .stunning-contact-item:hover {
          background: rgba(96, 82, 165, 0.08);
          transform: translateX(4px);
        }

        .stunning-contact-icon {
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-primary);
          box-shadow: 0 2px 8px rgba(96, 82, 165, 0.1);
          flex-shrink: 0;
        }

        .stunning-contact-text {
          flex: 1;
          text-align: left;
        }

        .stunning-contact-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 2px;
        }

        .stunning-contact-value {
          font-size: 15px;
          font-weight: 600;
          color: var(--color-text-dark);
          word-break: break-word;
        }

        .stunning-quick-stats {
          background: white;
          border-radius: 24px;
          padding: 32px;
          box-shadow: 0 10px 40px rgba(96, 82, 165, 0.12);
          border: 1px solid rgba(96, 82, 165, 0.1);
          animation: slideInRight 0.7s ease-out;
        }

        .stunning-stats-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--color-text-dark);
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .stunning-stats-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 4px 16px rgba(96, 82, 165, 0.3);
        }

        .stunning-stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .stunning-stat-box {
          background: #cfc9ff3b;
          padding: 20px;
          border-radius: 16px;
          border: 2px solid transparent;
          transition: all 0.3s ease;
        }

        .stunning-stat-box:hover {
          border-color: var(--color-primary);
          box-shadow: 0 8px 24px rgba(96, 82, 165, 0.15);
          transform: translateY(-4px);
        }

        .stunning-stat-label {
          font-size: 13px;
          font-weight: 600;
          color: var(--color-text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .stunning-stat-value {
          font-size: 18px;
          font-weight: 700;
          color: var(--color-primary);
        }

        .stunning-content-section {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          animation: fadeInUp 0.8s ease-out;
        }

        .stunning-info-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(96, 82, 165, 0.1);
          border: 1px solid rgba(96, 82, 165, 0.08);
          transition: all 0.4s ease;
        }

        .stunning-info-card:hover {
          box-shadow: 0 16px 48px rgba(96, 82, 165, 0.18);
          transform: translateY(-6px);
        }

        .stunning-card-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid var(--color-secondary);
        }

        .stunning-card-icon {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 4px 16px rgba(96, 82, 165, 0.3);
        }

        .stunning-card-title {
          font-size: 20px;
          font-weight: 700;
          color: var(--color-text-dark);
        }

        .stunning-detail-grid {
          display: grid;
          gap: 16px;
        }

        .stunning-detail-item {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 16px;
          background: #cfc9ff3b;
          border-radius: 12px;
          transition: all 0.3s ease;
          border-left: 3px solid transparent;
        }

        .stunning-detail-item:hover {
          background: white;
          border-left-color: var(--color-primary);
          box-shadow: 0 4px 16px rgba(96, 82, 165, 0.1);
          transform: translateX(6px);
        }

        .stunning-detail-icon {
          width: 38px;
          height: 38px;
          background: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-primary);
          box-shadow: 0 2px 8px rgba(96, 82, 165, 0.12);
          flex-shrink: 0;
        }

        .stunning-detail-content {
          flex: 1;
          min-width: 0;
        }

        .stunning-detail-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--color-text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .stunning-detail-value {
          font-size: 15px;
          font-weight: 600;
          color: var(--color-text-dark);
          word-break: break-word;
          line-height: 1.5;
        }

        .stunning-full-width {
          grid-column: 1 / -1;
        }

        @media (max-width: 1200px) {
          .stunning-hero-section {
            grid-template-columns: 1fr;
          }

          .stunning-profile-card {
            animation: fadeInUp 0.7s ease-out;
          }

          .stunning-quick-stats {
            animation: fadeInUp 0.8s ease-out;
          }
        }

        @media (max-width: 968px) {
          .stunning-content-section {
            grid-template-columns: 1fr;
          }

          .stunning-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .stunning-profile-container {
            padding: 20px 16px;
          }

          .stunning-hero-section {
            gap: 20px;
            margin-bottom: 20px;
          }

          .stunning-profile-card {
            padding: 32px 24px;
          }

          .stunning-profile-card::before {
            height: 120px;
          }

          .stunning-profile-image,
          .stunning-photo-wrapper {
            width: 120px;
            height: 120px;
          }

          .stunning-profile-name {
            font-size: 24px;
          }

          .stunning-quick-stats {
            padding: 24px;
          }

          .stunning-stats-title {
            font-size: 20px;
          }

          .stunning-stats-grid {
            gap: 12px;
          }

          .stunning-stat-box {
            padding: 16px;
          }

          .stunning-content-section {
            gap: 20px;
          }

          .stunning-info-card {
            padding: 24px;
          }

          .stunning-card-header {
            gap: 12px;
          }

          .stunning-card-icon {
            width: 46px;
            height: 46px;
          }

          .stunning-card-title {
            font-size: 18px;
          }

          .stunning-detail-grid {
            gap: 12px;
          }

          .stunning-detail-item {
            padding: 14px;
          }
        }

        /* KYC Documents Section */
        .kyc-docs-section {
          margin-top: 20px;
        }

        .kyc-docs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 12px;
        }

        .kyc-doc-card {
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          transition: box-shadow 0.2s ease;
        }

        .kyc-doc-card:hover {
          box-shadow: 0 4px 16px rgba(96, 82, 165, 0.15);
        }

        .kyc-doc-label {
          padding: 6px 10px;
          background: #f3f4f6;
          color: #555;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          display: flex;
          align-items: center;
          gap: 5px;
          border-bottom: 1px solid #e5e7eb;
        }

        .kyc-doc-img-wrapper {
          position: relative;
          height: 120px;
          background: #f9fafb;
          overflow: hidden;
        }

        .kyc-doc-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .kyc-doc-view-overlay {
          position: absolute;
          inset: 0;
          background: rgba(96, 82, 165, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s ease;
          cursor: pointer;
          text-decoration: none;
        }

        .kyc-doc-card:hover .kyc-doc-view-overlay {
          opacity: 1;
        }

        .kyc-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 14px;
        }

        .kyc-status-badge.pending { background: #fff3cd; color: #856404; }
        .kyc-status-badge.approved { background: #d1e7dd; color: #0a3622; }
        .kyc-status-badge.rejected { background: #f8d7da; color: #58151c; }

        @media (max-width: 480px) {
          .stunning-profile-container {
            padding: 16px 12px;
          }

          .stunning-profile-card {
            padding: 24px 20px;
            border-radius: 20px;
          }

          .stunning-profile-image,
          .stunning-photo-wrapper {
            width: 100px;
            height: 100px;
          }

          .stunning-profile-name {
            font-size: 20px;
          }

          .stunning-contact-item {
            padding: 12px 16px;
          }

          .stunning-contact-icon {
            width: 36px;
            height: 36px;
          }

          .stunning-contact-value {
            font-size: 14px;
          }

          .stunning-quick-stats {
            padding: 20px;
            border-radius: 20px;
          }

          .stunning-stats-grid {
            grid-template-columns: 1fr;
          }

          .stunning-stat-box {
            padding: 14px;
          }

          .stunning-info-card {
            padding: 20px;
            border-radius: 16px;
          }

          .stunning-card-icon {
            width: 42px;
            height: 42px;
          }

          .stunning-card-title {
            font-size: 17px;
          }

          .stunning-detail-item {
            padding: 12px;
            gap: 12px;
          }

          .stunning-detail-icon {
            width: 34px;
            height: 34px;
          }

          .stunning-detail-value {
            font-size: 14px;
          }
        }
      `}</style>

      <div className="stunning-profile-container">
        <div className="stunning-profile-wrapper">
          
          {/* Hero Section - Profile Card + Quick Stats */}
          <div className="stunning-hero-section">
            {/* Left Side - Profile Card */}
            <div className="stunning-profile-card">
              <div className="stunning-profile-image">
                <div className="stunning-photo-wrapper">
                  {merchant.ownerPhoto ? (
                    <img
                      src={imgUrl(merchant.ownerPhoto)}
                      alt="Owner"
                      className="stunning-photo"
                    />
                  ) : (
                    <User size={56} className="stunning-photo-icon" />
                  )}
                </div>
              </div>

              <h1 className="stunning-profile-name">{merchant.fullName || "N/A"}</h1>

              {merchant.uniqueId && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '20px',
                  padding: '5px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#444',
                  marginBottom: '16px',
                  position: 'relative',
                  zIndex: 2
                }}>
                  <FileText size={14} color="#5b4ef3" />
                  {merchant.uniqueId}
                </div>
              )}

              <div className="stunning-contact-item">
                <div className="stunning-contact-icon">
                  <Phone size={18} />
                </div>
                <div className="stunning-contact-text">
                  <div className="stunning-contact-label">Phone</div>
                  <div className="stunning-contact-value">{merchant.contactNumber || "N/A"}</div>
                </div>
              </div>

              <div className="stunning-contact-item">
                <div className="stunning-contact-icon">
                  <Mail size={18} />
                </div>
                <div className="stunning-contact-text">
                  <div className="stunning-contact-label">Email</div>
                  <div className="stunning-contact-value">{merchant.email || "N/A"}</div>
                </div>
              </div>
            </div>

            {/* Right Side - Quick Stats */}
            <div className="stunning-quick-stats">
              <div className="stunning-stats-title">
                <div className="stunning-stats-icon">
                  <BriefcaseBusiness size={24} />
                </div>
                Business Overview
              </div>

              <div className="stunning-stats-grid">
                <div className="stunning-stat-box">
                  <div className="stunning-stat-label">Business Name</div>
                  <div className="stunning-stat-value">{merchant.businessName || "N/A"}</div>
                </div>

                <div className="stunning-stat-box">
                  <div className="stunning-stat-label">Business Type</div>
                  <div className="stunning-stat-value">{merchant.businessTypeId?.name || "N/A"}</div>
                </div>
                 <div className="stunning-stat-box">
                  <div className="stunning-stat-label">Business Category</div>
                  <div className="stunning-stat-value">{merchant.businessCategoryId?.name || "N/A"}</div>
                </div>

                <div className="stunning-stat-box">
                  <div className="stunning-stat-label">GSTIN Number</div>
                  <div className="stunning-stat-value">{merchant.gstinNumber || "N/A"}</div>
                </div>

                <div className="stunning-stat-box">
                  <div className="stunning-stat-label">Country</div>
                  <div className="stunning-stat-value">{merchant.countryId?.name || "N/A"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section - Information Cards */}
          <div className="stunning-content-section">
            
            {/* Personal Information Card */}
            <div className="stunning-info-card">
              <div className="stunning-card-header">
                <div className="stunning-card-icon">
                  <User size={24} />
                </div>
                <div className="stunning-card-title">Personal Details</div>
              </div>

              <div className="stunning-detail-grid">
                <div className="stunning-detail-item">
                  <div className="stunning-detail-icon">
                    <User size={18} />
                  </div>
                  <div className="stunning-detail-content">
                    <div className="stunning-detail-label">Full Name</div>
                    <div className="stunning-detail-value">{merchant.fullName || "N/A"}</div>
                  </div>
                </div>

                <div className="stunning-detail-item">
                  <div className="stunning-detail-icon">
                    <Phone size={18} />
                  </div>
                  <div className="stunning-detail-content">
                    <div className="stunning-detail-label">Contact Number</div>
                    <div className="stunning-detail-value">{merchant.contactNumber || "N/A"}</div>
                  </div>
                </div>

                <div className="stunning-detail-item">
                  <div className="stunning-detail-icon">
                    <Mail size={18} />
                  </div>
                  <div className="stunning-detail-content">
                    <div className="stunning-detail-label">Email Address</div>
                    <div className="stunning-detail-value">{merchant.email || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Information Card */}
            {/* <div className="stunning-info-card">
              <div className="stunning-card-header">
                <div className="stunning-card-icon">
                  <Building size={24} />
                </div>
                <div className="stunning-card-title">Business Details</div>
              </div>

              <div className="stunning-detail-grid">
                <div className="stunning-detail-item">
                  <div className="stunning-detail-icon">
                    <Building size={18} />
                  </div>
                  <div className="stunning-detail-content">
                    <div className="stunning-detail-label">Business Name</div>
                    <div className="stunning-detail-value">{merchant.businessName || "N/A"}</div>
                  </div>
                </div>

                <div className="stunning-detail-item">
                  <div className="stunning-detail-icon">
                    <Layers size={18} />
                  </div>
                  <div className="stunning-detail-content">
                    <div className="stunning-detail-label">Business Type</div>
                    <div className="stunning-detail-value">{merchant.businessTypeId?.name || "N/A"}</div>
                  </div>
                </div>

                <div className="stunning-detail-item">
                  <div className="stunning-detail-icon">
                    <FileText size={18} />
                  </div>
                  <div className="stunning-detail-content">
                    <div className="stunning-detail-label">GSTIN Number</div>
                    <div className="stunning-detail-value">{merchant.gstinNumber || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Address Information Card - Full Width */}
            <div className="stunning-info-card ">
              <div className="stunning-card-header">
                <div className="stunning-card-icon">
                  <MapPin size={24} />
                </div>
                <div className="stunning-card-title">Address Information</div>
              </div>

              <div className="stunning-detail-grid">
                <div className="stunning-detail-item">
                  <div className="stunning-detail-icon">
                    <MapPin size={18} />
                  </div>
                  <div className="stunning-detail-content">
                    <div className="stunning-detail-label">Street Address</div>
                    <div className="stunning-detail-value">{merchant.streetAddress || "N/A"}</div>
                  </div>
                </div>

                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px'}}>
                  <div className="stunning-detail-item">
                    <div className="stunning-detail-icon">
                      <Flag size={18} />
                    </div>
                    <div className="stunning-detail-content">
                      <div className="stunning-detail-label">Country</div>
                      <div className="stunning-detail-value">{merchant.countryId?.name || "N/A"}</div>
                    </div>
                  </div>

                  <div className="stunning-detail-item">
                    <div className="stunning-detail-icon">
                      <Building size={18} />
                    </div>
                    <div className="stunning-detail-content">
                      <div className="stunning-detail-label">State</div>
                      <div className="stunning-detail-value">{merchant.stateId?.name || "N/A"}</div>
                    </div>
                  </div>

                  <div className="stunning-detail-item">
                    <div className="stunning-detail-icon">
                      <MapPin size={18} />
                    </div>
                    <div className="stunning-detail-content">
                      <div className="stunning-detail-label">District</div>
                      <div className="stunning-detail-value">{merchant.districtId?.name || "N/A"}</div>
                    </div>
                  </div>

                  <div className="stunning-detail-item">
                    <div className="stunning-detail-icon">
                      <MapPin size={18} />
                    </div>
                    <div className="stunning-detail-content">
                      <div className="stunning-detail-label">Local Area</div>
                      <div className="stunning-detail-value">{merchant.localAreaId?.name || "N/A"}</div>
                    </div>
                  </div>





                </div>
              </div>
            </div>

            {/* Social Information Card */}
            <div className="stunning-info-card stunning-full-width">
              <div className="stunning-card-header">
                <div className="stunning-card-icon">
                  <Share2 size={24} />
                </div>
                <div className="stunning-card-title">Social Media</div>
              </div>

              <div className="stunning-detail-grid">
                <div className="stunning-detail-item">
                  <div className="stunning-detail-icon">
                    <Link2 size={18} />
                  </div>
                  <div className="stunning-detail-content">
                    <div className="stunning-detail-label">Social Media Link</div>
                    <div className="stunning-detail-value">
                      {merchant.socialMediaLinks?.[0] || "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* KYC Documents Section */}
          {(merchant.ownerPhoto || merchant.businessLogo || merchant.aadhaarCardFront || merchant.aadhaarCardBack || merchant.panCardFront || merchant.panCardBack) && (
            <div className="kyc-docs-section">
              <div className="stunning-info-card">
                <div className="stunning-card-header">
                  <div className="stunning-card-icon">
                    <ShieldCheck size={22} />
                  </div>
                  <div>
                    <div className="stunning-card-title">KYC Documents</div>
                    {merchant.status && (
                      <span className={`kyc-status-badge ${merchant.status}`}>
                        <ShieldCheck size={12} />
                        {merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="kyc-docs-grid">
                  {[
                    { key: 'ownerPhoto',       label: 'Owner Photo',        icon: <User size={11} /> },
                    { key: 'businessLogo',     label: 'Business Logo',      icon: <Building size={11} /> },
                    { key: 'aadhaarCardFront', label: 'Aadhaar Front',      icon: <ShieldCheck size={11} /> },
                    { key: 'aadhaarCardBack',  label: 'Aadhaar Back',       icon: <ShieldCheck size={11} /> },
                    { key: 'panCardFront',     label: 'PAN Card Front',     icon: <CreditCard size={11} /> },
                    { key: 'panCardBack',      label: 'PAN Card Back',      icon: <CreditCard size={11} /> },
                  ].filter(doc => merchant[doc.key]).map(doc => (
                    <div key={doc.key} className="kyc-doc-card">
                      <div className="kyc-doc-label">{doc.icon} {doc.label}</div>
                      <div className="kyc-doc-img-wrapper">
                        <img
                          src={imgUrl(merchant[doc.key])}
                          alt={doc.label}
                          className="kyc-doc-img"
                        />
                        <a
                          href={imgUrl(merchant[doc.key])}
                          target="_blank"
                          rel="noreferrer"
                          className="kyc-doc-view-overlay"
                        >
                          <Eye size={22} color="white" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default MerchantProfile;